import PropTypes from 'prop-types';
import './ConfirmationDialog.scss';
import Button, { Colors, Variants } from '../../atoms/Button';
import { useEffect } from 'react';

function ConfirmationDialogView({
  dialogRef = null,
  open = false, // * Note: This is NOT passed directly to the <dialog> tag
  message = '',
  onCancel = () => { },
  onConfirm = () => { },
  confirmText = 'Confirm',
  confirmColor = Colors.Success,
  cancelText = 'Cancel',
}) {
  useEffect(() => {
    // when the user of this component changes the open prop, hide/show the modal
    if (dialogRef?.current) {
      if (open) {
        dialogRef.current.showModal();
      } else {
        dialogRef.current.close();
      }
    }
  }, [open, dialogRef]);

  const onCloseDialog = () => {
    if (!dialogRef?.current) {
      return;
    }

    dialogRef.current.close();
  };

  const onCancelClick = () => {
    onCancel();
    onCloseDialog();
  };

  const onConfirmClick = () => {
    onConfirm();
    onCloseDialog();
  };

  const renderMessage = () => {
    if (typeof message === 'string') {
      return message.split('\n').map((line, i) => {
        return (
          <div key={i} className="ConfirmationDialog-messageLine">
            {line}
          </div>
        );
      });
    }
    return message;
  };

  /**
   * Reason we do not pass the 'open' prop along:
   * > It is recommended to use the .show() or .showModal() method to render dialogs, rather than the open attribute.
   * > If a <dialog> is opened using the open attribute, it is non-modal.
   * source: https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Elements/dialog
   *
   * Tested and behaviour was not as expected when using open attribute.
   */
  return (
    <dialog ref={dialogRef} className="ConfirmationDialog">
      <div className="ConfirmationDialog-message">{renderMessage()}</div>
      <div className="ConfirmationDialog-buttons">
        <Button onClick={onCancelClick} content={cancelText} />
        <Button onClick={onConfirmClick} variant={Variants.Vivid} color={confirmColor} content={confirmText} />
      </div >
    </dialog >
  );
}

ConfirmationDialogView.propTypes = {
  dialogRef: PropTypes.object.isRequired,
  open: PropTypes.bool,
  message: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  onCancel: PropTypes.func,
  onConfirm: PropTypes.func,

  confirmText: PropTypes.string,
  confirmColor: PropTypes.oneOf(Object.values(Colors)),
  cancelText: PropTypes.string,
};

export default ConfirmationDialogView;
